import * as pdfjsLib from 'pdfjs-dist';
import DrawIcon from '@mui/icons-material/Draw';
import GestureIcon from '@mui/icons-material/Gesture';
import EventIcon from '@mui/icons-material/Event';
import BadgeIcon from '@mui/icons-material/Badge';

// Load the pdf.js worker matching the runtime API version (avoids version-mismatch
// when different checkouts resolve different pdfjs-dist versions).
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export const KINDS = [
  { key: 'signature', label: 'Signature', short: 'Sign', color: '#1c4b82', icon: DrawIcon },
  { key: 'initials', label: 'Initials', short: 'Initial', color: '#0a7f4b', icon: GestureIcon },
  { key: 'date', label: 'Date', short: 'Date', color: '#b06f00', icon: EventIcon },
  { key: 'printName', label: 'Print name', short: 'Name', color: '#6b4fa0', icon: BadgeIcon },
];
export const kindMeta = (k) => KINDS.find((x) => x.key === k) || KINDS[0];

// Sample marks drawn inside each block, resting on the block's bottom edge —
// exactly where the signer's mark lands (see esign/stamp.js stampSession and the
// agora signing page). Calibrate against these, not against an empty rectangle.
export const CURSIVE = '"Snell Roundhand", "Brush Script MT", "Segoe Script", cursive';
export const SAMPLE = { signature: 'Signature', initials: 'JT', date: '01/31/26', printName: 'Print Name' };

export const b64ToBytes = (b64) => {
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

let idSeq = 0;
export const newId = () => `f${Date.now()}_${idSeq++}`;

// Render every page of a PDF to the { page, widthPts, heightPts } shape the
// placement canvas expects. Accepts a URL string or raw bytes.
export const renderPdf = async (bytesOrUrl) => {
  const task = typeof bytesOrUrl === 'string'
    ? pdfjsLib.getDocument(bytesOrUrl)
    : pdfjsLib.getDocument({ data: bytesOrUrl });
  const pdf = await task.promise;
  const infos = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 1 });
    infos.push({ page, widthPts: vp.width, heightPts: vp.height });
  }
  return infos;
};

// Pages render as stacked canvases with `overflow: visible`, so a field dragged
// past its page boundary still *looks* right but keeps the wrong `page` with an
// out-of-range `y`. Re-map each field to the page it visually sits on (with an
// in-range y) so stamping and the signer view land on the correct page. This is
// a no-op for fields already within their page, so saving is idempotent.
export const normalizeFieldPage = (f, pages, scale) => {
  if (!pages.length) return f;
  const MARGIN = 24; // CanvasPage `mb: 3` → theme spacing(3) = 24px between pages
  const pageTop = [];
  let acc = 0;
  for (let i = 0; i < pages.length; i++) { pageTop[i] = acc; acc += pages[i].heightPts * scale + MARGIN; }
  const srcP = Math.min(Math.max(f.page || 0, 0), pages.length - 1);
  const absTop = pageTop[srcP] + (pages[srcP].heightPts - (f.y + f.h)) * scale;
  let vp = 0;
  for (let i = 0; i < pages.length; i++) { if (absTop >= pageTop[i] - 0.5) vp = i; }
  let localTopPx = absTop - pageTop[vp];
  const maxTopPx = Math.max((pages[vp].heightPts - f.h) * scale, 0);
  localTopPx = Math.min(Math.max(localTopPx, 0), maxTopPx);
  return { ...f, page: vp, y: pages[vp].heightPts - localTopPx / scale - f.h };
};

// Round-trip a field list into the shape the backend persists.
export const toFieldMap = (fields) => fields.map(({ id, page, x, y, w, h, role, kind, required, dateFormat }) => ({
  id,
  page,
  x: Math.round(x),
  y: Math.round(y),
  w: Math.round(w),
  h: Math.round(h),
  role,
  kind,
  required: required !== false,
  ...(kind === 'date' ? { dateFormat: dateFormat || 'MM/DD/YYYY' } : {}),
}));
